import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from './App';

// ─── Subject border class map ─────────────────────────────────────────────────

const SUBJECT_BORDER = {
  'Business Law and Practice':            'b-blp',
  'Property Law and Practice':            'b-property',
  'Contract Law':                          'b-contract',
  'Tort':                                  'b-tort',
  'Dispute Resolution':                    'b-dispute',
  'Criminal Law and Practice':            'b-criminal',
  'Wills and Administration of Estates':  'b-wills',
  'Wills and the Administration of Estates': 'b-wills',
  'Solicitors Accounts':                  'b-accounts',
  'Land Law':                             'b-land',
  'Trusts Law':                           'b-trusts',
  'Legal Services':                       'b-legal-services',
  'Legal System':                         'b-legal-system',
  'Constitutional and Administrative Law':'b-const',
};

const SUBJECT_DOT = {
  'Business Law and Practice':            'var(--subj-blp)',
  'Property Law and Practice':            'var(--subj-property)',
  'Contract Law':                          'var(--subj-contract)',
  'Tort':                                  'var(--subj-tort)',
  'Dispute Resolution':                    'var(--subj-dispute)',
  'Criminal Law and Practice':            'var(--subj-criminal)',
  'Wills and Administration of Estates':  'var(--subj-wills)',
  'Wills and the Administration of Estates': 'var(--subj-wills)',
  'Solicitors Accounts':                  'var(--subj-accounts)',
  'Land Law':                             'var(--subj-land)',
  'Trusts Law':                           'var(--subj-trusts)',
  'Legal Services':                       'var(--subj-legal-services)',
  'Legal System':                         'var(--subj-legal-system)',
  'Constitutional and Administrative Law':'var(--subj-const)',
};

const SUBJECT_ABBREV = {
  'Business Law and Practice':            'BLP',
  'Property Law and Practice':            'Property',
  'Contract Law':                          'Contract',
  'Tort':                                  'Tort',
  'Dispute Resolution':                    'Dispute',
  'Criminal Law and Practice':            'Criminal',
  'Wills and Administration of Estates':  'Wills',
  'Wills and the Administration of Estates': 'Wills',
  'Solicitors Accounts':                  'Accounts',
  'Land Law':                             'Land Law',
  'Trusts Law':                           'Trusts',
  'Legal Services':                       'Legal Serv.',
  'Legal System':                         'Legal Sys.',
  'Constitutional and Administrative Law':'Const. & Admin.',
};

function subjectBorder(sa) {
  if (!sa) return '';
  if (SUBJECT_BORDER[sa]) return SUBJECT_BORDER[sa];
  for (const [k, v] of Object.entries(SUBJECT_BORDER)) {
    if (sa.toLowerCase().includes(k.toLowerCase())) return v;
  }
  return '';
}
function subjectDot(sa) {
  if (!sa) return 'var(--text-tertiary)';
  if (SUBJECT_DOT[sa]) return SUBJECT_DOT[sa];
  for (const [k, v] of Object.entries(SUBJECT_DOT)) {
    if (sa.toLowerCase().includes(k.toLowerCase())) return v;
  }
  return 'var(--text-tertiary)';
}
function subjectAbbrev(sa) {
  if (!sa) return '';
  if (SUBJECT_ABBREV[sa]) return SUBJECT_ABBREV[sa];
  for (const [k, v] of Object.entries(SUBJECT_ABBREV)) {
    if (sa.toLowerCase().includes(k.toLowerCase())) return v;
  }
  return sa;
}

function overlayValueClass(pct) {
  if (pct >= 70) return 'overlay-value overlay-value-green';
  if (pct >= 40) return 'overlay-value overlay-value-amber';
  return 'overlay-value overlay-value-red';
}

// ─── Depth pips ───────────────────────────────────────────────────────────────
// Maps principle_count → 4 pip array + weight label matching v3 reference

function depthPipsData(pc) {
  if (pc == null) return null;
  if (pc <= 7)  return { pips: ['light','off','off','off'], label: 'Light' };
  if (pc <= 14) return { pips: ['med','med','off','off'],   label: 'Medium' };
  return         { pips: ['heavy','heavy','heavy','heavy'], label: 'Heavy' };
}

function DepthPips({ principleCount }) {
  const data = depthPipsData(principleCount);
  if (!data) return null;
  return (
    <>
      <span className="depth-pips">
        {data.pips.map((p, i) => <span key={i} className={`depth-pip depth-pip-${p}`} />)}
      </span>
      <span className="depth-text">{data.label}</span>
    </>
  );
}

// ─── Conf colour helpers ──────────────────────────────────────────────────────

function confDoneStyle(confFraction) {
  const pct = Math.round(confFraction * 100);
  if (pct >= 70) return { background: '#DCFCE7', color: '#166534' };
  if (pct >= 40) return { background: '#FEF3C7', color: '#92400E' };
  return { background: '#FEE2E2', color: '#991B1B' };
}

function revWashClass(topicConf) {
  if (topicConf == null) return 'day-card-rev-amber';
  return topicConf < 0.5 ? 'day-card-rev-red' : 'day-card-rev-amber';
}

function weekRevWashClass(topicConf) {
  if (topicConf == null) return 'week-card-rev-amber';
  return topicConf < 0.40 ? 'week-card-rev-red' : 'week-card-rev-amber';
}

function confClass(conf, prefix) {
  if (conf >= 0.7) return `${prefix}-green`;
  if (conf >= 0.4) return `${prefix}-amber`;
  return `${prefix}-red`;
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

function dateToStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function todayStr() { return dateToStr(new Date()); }
function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return dateToStr(d);
}
function weekStartOf(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay();
  d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return dateToStr(d);
}
function formatWeekLabel(ws) {
  return new Date(ws + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}
function shortDayLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' })}`;
}
function longDayLabel(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

// ─── Entry filtering / sorting ────────────────────────────────────────────────

function applyFlkFilter(entries, filter) {
  if (!filter || filter === 'all') return entries;
  return entries.filter(e => (e.module || '').toUpperCase().includes(filter));
}
function applySortEntries(entries, sort) {
  if (!sort || sort === 'priority') return entries;
  const s = [...entries];
  if (sort === 'subject') s.sort((a, b) => (a.subject_area || '').localeCompare(b.subject_area || ''));
  else if (sort === 'lightest') s.sort((a, b) => (a.principle_count || 0) - (b.principle_count || 0));
  else if (sort === 'heaviest') s.sort((a, b) => (b.principle_count || 0) - (a.principle_count || 0));
  return s;
}

// ─── CompleteOverlay ──────────────────────────────────────────────────────────

function CompleteOverlay({ entry, onConfirm, onCancel }) {
  const [conf, setConf] = useState(50);
  const [saving, setSaving] = useState(false);
  const sliderBg = `linear-gradient(to right, var(--blue) 0%, var(--blue) ${conf}%, var(--border) ${conf}%, var(--border) 100%)`;

  return (
    <div className="overlay-backdrop" onClick={!saving ? onCancel : undefined}>
      <div className="overlay-card" onClick={e => e.stopPropagation()}>
        <div className="overlay-name">{entry.subtopic}</div>
        {entry.subject_area && <div className="overlay-subject">{entry.subject_area}</div>}
        <div className="overlay-label">Confidence</div>
        <input
          type="range" min="0" max="100" step="5"
          value={conf}
          onChange={e => setConf(parseInt(e.target.value))}
          className="overlay-slider"
          style={{ background: sliderBg }}
        />
        <div className="overlay-range"><span>0</span><span>100</span></div>
        <div className={overlayValueClass(conf)}>{conf}%</div>
        <button
          className="btn-primary-full"
          disabled={saving}
          onClick={async () => { setSaving(true); await onConfirm(conf / 100); }}
          style={{ marginBottom: 8 }}
        >
          {saving ? 'Saving' : 'Done'}
        </button>
        <button className="btn-ghost-full" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── AddTopicPopover ──────────────────────────────────────────────────────────

function AddTopicPopover({ dateStr, onAdd, onClose, openUp }) {
  const [mode, setMode] = useState(null);
  const [autoError, setAutoError] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const handleAuto = async () => {
    setMode('auto'); setAutoLoading(true);
    const res = await api('/api/planner/add-topic', {
      method: 'POST', body: JSON.stringify({ date: dateStr, mode: 'auto' }),
    });
    setAutoLoading(false);
    if (res.ok) { onAdd(res.entry); onClose(); } else { setAutoError(res.error || 'No topics available'); }
  };

  const handleSearch = async q => {
    setSearch(q);
    if (q.length < 2) { setTopics([]); return; }
    setTopicsLoading(true);
    const [f1, f2] = await Promise.all([api('/api/topics/FLK1'), api('/api/topics/FLK2')]);
    const all = [...(Array.isArray(f1) ? f1 : []), ...(Array.isArray(f2) ? f2 : [])];
    const ql = q.toLowerCase();
    setTopics(all.filter(t =>
      (t.subtopic || '').toLowerCase().includes(ql) ||
      (t.display_name || '').toLowerCase().includes(ql) ||
      (t.subject_area || '').toLowerCase().includes(ql)
    ).slice(0, 12));
    setTopicsLoading(false);
  };

  const handleChoose = async topic => {
    const res = await api('/api/planner/add-topic', {
      method: 'POST',
      body: JSON.stringify({ date: dateStr, mode: 'choose', subtopic: topic.subtopic, module: topic.module }),
    });
    if (res.ok) { onAdd(res.entry); onClose(); }
  };

  return (
    <div className={`planner-add-popover${openUp ? ' planner-add-popover--up' : ''}`} ref={ref}>
      {mode === null && (
        <>
          <div className="planner-add-popover-title">Add a topic</div>
          <button className="planner-add-opt-btn" onClick={handleAuto}>
            <div className="planner-add-opt-label">Auto-suggest</div>
            <div className="planner-add-opt-desc">Pick the next priority topic</div>
          </button>
          <button className="planner-add-opt-btn" onClick={() => setMode('choose')}>
            <div className="planner-add-opt-label">Choose topic</div>
            <div className="planner-add-opt-desc">Search and pick any topic</div>
          </button>
        </>
      )}
      {mode === 'auto' && autoLoading && <div className="planner-add-loading">Finding next topic</div>}
      {mode === 'auto' && !autoLoading && autoError && <div className="planner-add-error">{autoError}</div>}
      {mode === 'choose' && (
        <>
          <div className="planner-add-popover-title">Choose a topic</div>
          <input className="planner-add-search" autoFocus placeholder="Search topics" value={search} onChange={e => handleSearch(e.target.value)} />
          {topicsLoading && <div className="planner-add-loading">Searching</div>}
          <div className="planner-add-results">
            {topics.map(t => (
              <button key={t.id} className="planner-add-result-btn" onClick={() => handleChoose(t)}>
                <span className="planner-add-result-name">{t.display_name || t.subtopic}</span>
                <span className="planner-add-result-meta">{t.module} · {t.subject_area}</span>
              </button>
            ))}
            {!topicsLoading && topics.length === 0 && search.length >= 2 && (
              <div className="planner-add-noresults">No topics found</div>
            )}
          </div>
          <button className="planner-add-back" onClick={() => setMode(null)}>Back</button>
        </>
      )}
    </div>
  );
}

// ─── Topic card — day view (v3) ───────────────────────────────────────────────

function DayCard({ entry, dateStr, allWeekDates, onComplete, onMove, isPast, toggles, onNavigateToTopic }) {
  const [completing, setCompleting] = useState(false);
  const [localEntry, setLocalEntry] = useState(entry);
  useEffect(() => setLocalEntry(entry), [entry]);

  const e = localEntry;
  const isCompleted = e.status === 'completed';
  const isMoved = e.status === 'moved';
  const isRevision = e.planner_type === 'revision';
  const borderCls = subjectBorder(e.subject_area);
  const dayIndex = allWeekDates.indexOf(dateStr);
  const canAct = !isPast && !isCompleted && !isMoved;
  const canNavigate = !!(onNavigateToTopic && e.topic_id);
  const topicConf = e.topic_confidence;
  const showL2 = toggles?.l2 !== false;
  const showDepth = toggles?.depth !== false;

  const handleConfirm = async conf => {
    const res = await api('/api/planner/complete', {
      method: 'POST',
      body: JSON.stringify({ schedule_entry_id: e.id, confidence: conf }),
    });
    if (res.ok) {
      setLocalEntry(prev => ({ ...prev, status: 'completed', confidence_on_complete: conf }));
      onComplete(e.id, conf, res.overall_progress);
    }
    setCompleting(false);
  };

  const clickProps = canNavigate
    ? { onClick: () => onNavigateToTopic(e.topic_id, e.module), style: { cursor: 'pointer' } }
    : {};

  const actionButtons = canAct ? (
    <>
      <button className="btn-tick" onClick={ev => { ev.stopPropagation(); setCompleting(true); }} title="Mark as done">
        <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
      </button>
      <div className="day-card-arrows">
        <button className="btn-arrow" disabled={dayIndex <= 0}
          onClick={ev => { ev.stopPropagation(); dayIndex > 0 && onMove(e.id, allWeekDates[dayIndex - 1]); }}>
          <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <button className="btn-arrow" disabled={dayIndex >= allWeekDates.length - 1}
          onClick={ev => { ev.stopPropagation(); dayIndex < allWeekDates.length - 1 && onMove(e.id, allWeekDates[dayIndex + 1]); }}>
          <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      </div>
    </>
  ) : null;

  // ── Completed ──
  if (isCompleted) {
    const confStyle = confDoneStyle(e.confidence_on_complete ?? 0);
    const confPct = Math.round((e.confidence_on_complete ?? 0) * 100);
    return (
      <div className={`day-card-done ${borderCls}`}>
        <div className="day-card-body" {...clickProps}>
          <div className="day-card-row1">
            <span className="day-card-name">{e.subtopic}</span>
            {e.confidence_on_complete != null && (
              <span className="day-card-done-conf" style={confStyle}>{confPct}%</span>
            )}
          </div>
          <div className="day-card-row2">
            <span className="day-card-subject">{e.subject_area}</span>
            {showL2 && e.general_topic && (
              <><span className="day-card-sep">·</span><span className="day-card-l2">{e.general_topic}</span></>
            )}
            {e.principle_count != null && (
              <><span className="day-card-sep">·</span><span className="day-card-princ">{e.principle_count} principles</span></>
            )}
          </div>
        </div>
        <div className="day-card-actions">
          <div className="btn-tick-done"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></div>
        </div>
      </div>
    );
  }

  // ── Moved ──
  if (isMoved) {
    return (
      <div className={`day-card-moved ${borderCls}`}>
        <div className="day-card-body">
          <div className="day-card-row1">
            <span className="day-card-name">{e.subtopic}</span>
            <span className="day-card-moved-label">Moved →</span>
          </div>
          <div className="day-card-row2">
            <span className="day-card-subject">{e.subject_area}</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Revision (pending) ──
  if (isRevision) {
    return (
      <>
        <div className={`day-card-rev ${revWashClass(topicConf)} ${borderCls}`} {...clickProps}>
          <div className="day-card-rev-body">
            <div className="day-card-row1">
              <span className="day-card-name">{e.subtopic}</span>
              {topicConf != null && (
                <span className={`day-card-conf ${confClass(topicConf, 'day-card-conf')}`}>
                  {Math.round(topicConf * 100)}%
                </span>
              )}
            </div>
            <div className="day-card-row2">
              <span className="day-card-subject">{e.subject_area}</span>
              {showL2 && e.general_topic && (
                <><span className="day-card-sep">·</span><span className="day-card-l2">{e.general_topic}</span></>
              )}
              {e.principle_count != null && (
                <><span className="day-card-sep">·</span><span className="day-card-princ">{e.principle_count} principles</span></>
              )}
              {showDepth && <DepthPips principleCount={e.principle_count} />}
              <span style={{ marginLeft: 'auto' }}><span className="day-card-revise-pill">Revise</span></span>
            </div>
          </div>
          <div className="day-card-rev-actions">
            {!isPast && actionButtons}
          </div>
        </div>
        {completing && <CompleteOverlay entry={e} onConfirm={handleConfirm} onCancel={() => setCompleting(false)} />}
      </>
    );
  }

  // ── New (pending) ──
  return (
    <>
      <div className={`day-card ${borderCls}`} {...clickProps}>
        <div className="day-card-body">
          <div className="day-card-row1">
            <span className="day-card-name">{e.subtopic}</span>
            <span className="day-card-new">New</span>
          </div>
          <div className="day-card-row2">
            <span className="day-card-subject">{e.subject_area}</span>
            {showL2 && e.general_topic && (
              <><span className="day-card-sep">·</span><span className="day-card-l2">{e.general_topic}</span></>
            )}
            {e.principle_count != null && (
              <><span className="day-card-sep">·</span><span className="day-card-princ">{e.principle_count} principles</span></>
            )}
            {showDepth && <DepthPips principleCount={e.principle_count} />}
          </div>
        </div>
        <div className="day-card-actions">
          {!isPast && actionButtons}
        </div>
      </div>
      {completing && <CompleteOverlay entry={e} onConfirm={handleConfirm} onCancel={() => setCompleting(false)} />}
    </>
  );
}

// ─── Week card (v3) ───────────────────────────────────────────────────────────

function WeekCard({ entry, dateStr, toggles, onSwitchToDayView }) {
  const e = entry;
  const isCompleted = e.status === 'completed';
  const isMoved = e.status === 'moved';
  const isRevision = e.planner_type === 'revision';
  const borderCls = subjectBorder(e.subject_area);
  const dot = subjectDot(e.subject_area);
  const abbrev = subjectAbbrev(e.subject_area);
  const topicConf = e.topic_confidence;
  const switchTo = () => onSwitchToDayView && onSwitchToDayView(dateStr);
  const showL2 = toggles?.l2 !== false;
  const showDepth = toggles?.depth !== false;

  // ── Completed ──
  if (isCompleted) {
    const confPct = Math.round((e.confidence_on_complete ?? 0) * 100);
    const confStyle = confDoneStyle(e.confidence_on_complete ?? 0);
    return (
      <div className={`week-card-done ${borderCls}`} onClick={switchTo} role="button" tabIndex={0}>
        <div className="week-card-row1">
          <span className="week-card-name">{e.subtopic}</span>
          {e.confidence_on_complete != null && (
            <span className="week-card-done-conf" style={confStyle}>{confPct}%</span>
          )}
        </div>
        <div className="week-card-row2">
          <div className="week-card-dot" style={{ background: dot }} />
          <span className="week-card-subject">{abbrev}</span>
        </div>
      </div>
    );
  }

  // ── Moved ──
  if (isMoved) {
    return (
      <div className={`week-card-moved ${borderCls}`}>
        <div className="week-card-row1">
          <span className="week-card-name">{e.subtopic}</span>
          <span className="week-card-moved-label">Moved</span>
        </div>
        <div className="week-card-row2">
          <div className="week-card-dot" style={{ background: dot }} />
          <span className="week-card-subject">{abbrev}</span>
        </div>
      </div>
    );
  }

  // ── Revision ──
  if (isRevision) {
    return (
      <div className={`week-card-rev ${weekRevWashClass(topicConf)} ${borderCls}`} onClick={switchTo} role="button" tabIndex={0}>
        <div className="week-card-row1" style={{ position: 'relative' }}>
          <span className="week-card-name">{e.subtopic}</span>
          {topicConf != null && (
            <span className={`week-card-conf ${confClass(topicConf, 'week-card-conf')}`}>
              {Math.round(topicConf * 100)}%
            </span>
          )}
        </div>
        <div className="week-card-row2" style={{ position: 'relative' }}>
          <div className="week-card-dot" style={{ background: dot }} />
          <span className="week-card-subject">{abbrev}</span>
          {showL2 && e.general_topic && (
            <span className="week-card-l2">&middot; {e.general_topic}</span>
          )}
        </div>
        <div className="week-card-row3" style={{ position: 'relative' }}>
          <DepthPips principleCount={e.principle_count} />
          <span className="week-card-revise-pill">Revise</span>
        </div>
      </div>
    );
  }

  // ── New ──
  return (
    <div className={`week-card ${borderCls}`} onClick={switchTo} role="button" tabIndex={0}
      onKeyDown={ev => ev.key === 'Enter' && switchTo()}>
      <div className="week-card-row1">
        <span className="week-card-name">{e.subtopic}</span>
        <span className="week-card-new">New</span>
      </div>
      <div className="week-card-row2">
        <div className="week-card-dot" style={{ background: dot }} />
        <span className="week-card-subject">{abbrev}</span>
        {showL2 && e.general_topic && (
          <span className="week-card-l2">· {e.general_topic}</span>
        )}
      </div>
      {showDepth && (
        <div className="week-card-row3">
          <DepthPips principleCount={e.principle_count} />
        </div>
      )}
    </div>
  );
}

// ─── DayColumn — week view ────────────────────────────────────────────────────

const WEEK_COL_MAX = 4;

function DayColumn({ dateStr, dayData, allWeekDates, onComplete, onMove, onAddEntry, isToday, isPast, flkFilter, toggles, onSwitchToDayView }) {
  const rawEntries = dayData.entries || [];
  const entries = applyFlkFilter(rawEntries, flkFilter);
  const { is_study_day } = dayData;
  const completed_count = entries.filter(e => e.status === 'completed').length;
  const total_count = entries.length;

  const d = new Date(dateStr + 'T00:00:00');
  const dayLabel = d.toLocaleDateString('en-GB', { weekday: 'short' });
  const dateLabel = d.getDate();
  const visibleEntries = entries.slice(0, WEEK_COL_MAX);
  const hiddenCount = entries.length - WEEK_COL_MAX;

  return (
    <div className={`week-col${isToday ? ' week-col-today' : ''}`}>
      <div className="week-col-head">{dayLabel} {dateLabel}</div>
      {is_study_day && total_count > 0 && (
        <div className="week-col-count">{completed_count} of {total_count}</div>
      )}
      {!is_study_day ? (
        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', padding: '4px 0' }}>Rest day</div>
      ) : (
        <>
          <div className="week-cards">
            {visibleEntries.map(entry => (
              <WeekCard
                key={entry.id}
                entry={entry}
                dateStr={dateStr}
                toggles={toggles}
                onSwitchToDayView={onSwitchToDayView}
              />
            ))}
          </div>
          {hiddenCount > 0 && (
            <div className="week-more" onClick={() => onSwitchToDayView && onSwitchToDayView(dateStr)}>
              +{hiddenCount} more
            </div>
          )}
          {/* +Add button only in day view — hidden in week view */}
        </>
      )}
    </div>
  );
}

// ─── DayViewPane ──────────────────────────────────────────────────────────────

function DayViewPane({ dateStr, dayData, allWeekDates, onComplete, onMove, onAddEntry,
  isToday, isPast, onPrev, onNext, daySort, setDaySort, hideCompleted, setHideCompleted,
  flkFilter, toggles, onNavigateToTopic }) {
  const [showAdd, setShowAdd] = useState(false);

  const { is_study_day } = dayData || {};
  const rawEntries = dayData?.entries || [];
  const filtered = applyFlkFilter(rawEntries, flkFilter);
  const sorted = applySortEntries(filtered, daySort);
  const pending = sorted.filter(e => e.status !== 'completed');
  const completed = sorted.filter(e => e.status === 'completed');
  const displayCompleted = hideCompleted ? [] : completed;

  const origTotal = filtered.length;
  const origCompleted = filtered.filter(e => e.status === 'completed').length;

  return (
    <div>
      {/* Day navigation */}
      <div className="day-nav" style={{ marginBottom: 14 }}>
        <button className="day-nav-btn" onClick={onPrev}>← {shortDayLabel(addDays(dateStr, -1))}</button>
        <div className="day-nav-center">
          {isToday && <div className="day-nav-today-badge">Today</div>}
          <div className="day-nav-title">{longDayLabel(dateStr)}</div>
          {origTotal > 0 && <div className="day-nav-count">{origCompleted} of {origTotal} done</div>}
        </div>
        <button className="day-nav-btn" onClick={onNext}>{shortDayLabel(addDays(dateStr, 1))} →</button>
      </div>

      {!dayData || is_study_day === false ? (
        <div className="day-view-rest">Rest day — no sessions scheduled</div>
      ) : (
        <>
          {/* Controls */}
          {origTotal > 0 && (
            <div className="day-controls">
              <span
                className="day-controls-link"
                onClick={() => setHideCompleted(v => !v)}
              >
                {hideCompleted ? 'Show completed' : 'Hide completed'}
              </span>
              <div className="day-controls-right">
                <select
                  className="day-sort"
                  value={daySort}
                  onChange={e => setDaySort(e.target.value)}
                >
                  <option value="priority">Priority order</option>
                  <option value="subject">By subject</option>
                  <option value="lightest">Lightest first</option>
                  <option value="heaviest">Heaviest first</option>
                </select>
              </div>
            </div>
          )}

          {/* Pending */}
          {pending.length > 0 && (
            <>
              <div className="section-label">Pending ({pending.length})</div>
              <div className="day-cards">
                {pending.map(entry => (
                  <DayCard
                    key={entry.id}
                    entry={entry}
                    dateStr={dateStr}
                    allWeekDates={allWeekDates}
                    onComplete={onComplete}
                    onMove={onMove}
                    isPast={isPast}
                    toggles={toggles}
                    onNavigateToTopic={onNavigateToTopic}
                  />
                ))}
              </div>
            </>
          )}

          {pending.length === 0 && displayCompleted.length === 0 && (
            <div className="day-view-empty">
              {isPast ? 'No topics were scheduled for this day.' : 'No topics scheduled. Add one below.'}
            </div>
          )}

          {/* Completed */}
          {displayCompleted.length > 0 && (
            <>
              <div className="section-label">Completed ({displayCompleted.length})</div>
              <div className="day-cards">
                {displayCompleted.map(entry => (
                  <DayCard
                    key={entry.id}
                    entry={entry}
                    dateStr={dateStr}
                    allWeekDates={allWeekDates}
                    onComplete={onComplete}
                    onMove={onMove}
                    isPast={isPast}
                    toggles={toggles}
                    onNavigateToTopic={onNavigateToTopic}
                  />
                ))}
              </div>
            </>
          )}

          {/* Add topic */}
          {!isPast && (
            <div className="day-view-add-wrap">
              <button className="btn-add" onClick={() => setShowAdd(v => !v)}>
                + Add a topic
              </button>
              {showAdd && (
                <AddTopicPopover
                  dateStr={dateStr}
                  onAdd={entry => { onAddEntry(dateStr, entry); setShowAdd(false); }}
                  onClose={() => setShowAdd(false)}
                  openUp={true}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── WeeklySummaryCard ────────────────────────────────────────────────────────

function WeeklySummaryCard({ summary, onDismiss }) {
  if (!summary || summary.topics_completed === 0) return null;
  const { topics_completed, topics_moved, avg_confidence, weakest_subject, weakest_subject_avg } = summary;
  return (
    <div className="planner-summary-card">
      <button className="planner-summary-dismiss" onClick={onDismiss}>×</button>
      <p className="planner-summary-text">
        Last week: <strong>{topics_completed} topics covered</strong>
        {topics_moved > 0 && `, ${topics_moved} moved forward`}.
        {avg_confidence != null && <> Average confidence: <strong>{Math.round(avg_confidence * 100)}%</strong>.</>}
        {weakest_subject && weakest_subject_avg != null && <> Weakest: <strong>{weakest_subject}</strong> ({Math.round(weakest_subject_avg * 100)}%).</>}
      </p>
    </div>
  );
}

// ─── FirstRunBanner ───────────────────────────────────────────────────────────

function FirstRunBanner({ total, onDismiss }) {
  return (
    <div className="planner-first-run-banner">
      <button className="planner-first-run-dismiss" onClick={onDismiss}>×</button>
      <p className="planner-first-run-text">
        Your revision plan is ready. {total} topics, ordered by importance. As you tick them off and add data, the plan will adjust. You can look ahead to any week, but future weeks are projections that will change.
      </p>
    </div>
  );
}

// ─── Main Timetable ───────────────────────────────────────────────────────────

export default function Timetable({ onNavigateToTopic }) {
  const today = todayStr();

  const [viewMode, setViewMode] = useState(() => window.innerWidth < 768 ? 'day' : 'week');
  const [currentWeekStart, setCurrentWeekStart] = useState(weekStartOf(today));
  const [currentDayStr, setCurrentDayStr] = useState(today);
  const [weekData, setWeekData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(true);
  const [firstRunDismissed, setFirstRunDismissed] = useState(
    () => localStorage.getItem('planner_first_run_dismissed') === '1'
  );
  const [showFirstRun, setShowFirstRun] = useState(false);

  const [flkFilter, setFlkFilter] = useState('all');
  const [daySort, setDaySort] = useState('priority');
  const [hideCompleted, setHideCompleted] = useState(false);

  // Detail toggles — persisted to localStorage
  const [toggles, setToggles] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('planner_toggles') || '{}');
      return {
        l2:        saved.l2        !== undefined ? saved.l2        : true,
        depth:     saved.depth     !== undefined ? saved.depth     : true,
        questions: saved.questions !== undefined ? saved.questions : false,
        lastseen:  saved.lastseen  !== undefined ? saved.lastseen  : false,
      };
    } catch { return { l2: true, depth: true, questions: false, lastseen: false }; }
  });

  const flipToggle = key => setToggles(prev => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem('planner_toggles', JSON.stringify(next)); } catch {}
    return next;
  });

  const weekGridRef = useRef(null);
  const [weekScrollLeft, setWeekScrollLeft] = useState(false);
  const [weekScrollRight, setWeekScrollRight] = useState(false);

  // Auto-advance to next week only when the entire current week is past
  // (threshold 7 = never fires mid-week, so Sunday stays on the current week)
  useEffect(() => {
    const ws = weekStartOf(today);
    let pastCount = 0;
    for (let i = 0; i < 7; i++) { if (addDays(ws, i) < today) pastCount++; }
    if (pastCount >= 7) {
      const nextWs = addDays(ws, 7);
      setCurrentWeekStart(nextWs);
      setCurrentDayStr(nextWs);
    }
  }, []); // eslint-disable-line

  // Force day view on mobile
  useEffect(() => {
    const onResize = () => { if (window.innerWidth < 768) setViewMode('day'); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []); // eslint-disable-line

  // Week grid scroll button visibility
  const updateWeekScroll = useCallback(() => {
    const el = weekGridRef.current;
    if (!el) return;
    setWeekScrollLeft(el.scrollLeft > 2);
    setWeekScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = weekGridRef.current;
    if (!el) return;
    updateWeekScroll();
    el.addEventListener('scroll', updateWeekScroll, { passive: true });
    window.addEventListener('resize', updateWeekScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', updateWeekScroll);
      window.removeEventListener('resize', updateWeekScroll);
    };
  }, [weekData, updateWeekScroll]);

  const scrollWeekGrid = dir => {
    weekGridRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  const loadWeek = useCallback(async ws => {
    setLoading(true); setError(null);
    try {
      const res = await api(`/api/planner/week?date=${ws}`);
      if (!res || res.error) { setError((res && res.error) || 'Could not load planner.'); return; }
      setWeekData(res);
      setProgress(res.overall_progress);
      if (res.first_run && !firstRunDismissed) setShowFirstRun(true);
      setCurrentDayStr(prev => {
        const dates = Object.keys(res.days || {}).sort();
        return dates.includes(prev) ? prev : (dates[0] || ws);
      });
    } catch { setError('Could not load planner. Check your connection and try again.'); }
    finally { setLoading(false); }
  }, [firstRunDismissed]);

  useEffect(() => { loadWeek(currentWeekStart); }, [currentWeekStart, loadWeek]);

  useEffect(() => {
    if (weekData?.is_current_week) {
      const prevWeek = addDays(currentWeekStart, -7);
      api(`/api/planner/summary?week_start=${prevWeek}`)
        .then(res => { if (res && !res.error) setSummary(res); })
        .catch(() => {});
    }
  }, [weekData?.is_current_week, currentWeekStart]);

  const handleComplete = useCallback((entryId, conf, newProgress) => {
    setWeekData(prev => {
      if (!prev) return prev;
      const days = { ...prev.days };
      for (const ds of Object.keys(days)) {
        const idx = days[ds].entries.findIndex(e => e.id === entryId);
        if (idx !== -1) {
          const updated = days[ds].entries.map((e, i) =>
            i === idx ? { ...e, status: 'completed', confidence_on_complete: conf } : e
          );
          days[ds] = { ...days[ds], entries: updated, completed_count: updated.filter(e => e.status === 'completed').length };
          break;
        }
      }
      return { ...prev, days };
    });
    if (newProgress) setProgress(newProgress);
  }, []);

  const handleMove = useCallback(async (entryId, newDate) => {
    const res = await api('/api/planner/move', {
      method: 'POST', body: JSON.stringify({ schedule_entry_id: entryId, new_date: newDate }),
    });
    if (res.ok) loadWeek(currentWeekStart);
  }, [currentWeekStart, loadWeek]);

  const handleAddEntry = useCallback((dateStr, newEntry) => {
    setWeekData(prev => {
      if (!prev || !prev.days[dateStr]) return prev;
      const entry = { id: -(Date.now()), ...newEntry, status: 'pending', confidence_on_complete: null };
      return {
        ...prev,
        days: {
          ...prev.days,
          [dateStr]: {
            ...prev.days[dateStr],
            entries: [...prev.days[dateStr].entries, entry],
            total_count: prev.days[dateStr].total_count + 1,
          },
        },
      };
    });
  }, []);

  const goToPrevWeek = () => {
    const ws = addDays(currentWeekStart, -7);
    const wsEnd = addDays(ws, 6);
    setCurrentWeekStart(ws);
    setCurrentDayStr(today >= ws && today <= wsEnd ? today : wsEnd);
  };
  const goToNextWeek = () => {
    const ws = addDays(currentWeekStart, 7);
    const wsEnd = addDays(ws, 6);
    setCurrentWeekStart(ws);
    setCurrentDayStr(today >= ws && today <= wsEnd ? today : ws);
  };
  const goToThisWeek = () => {
    setCurrentWeekStart(weekStartOf(today));
    setCurrentDayStr(today);
  };
  const navigateToDay = dateStr => {
    const ws = weekStartOf(dateStr);
    setCurrentDayStr(dateStr);
    if (ws !== currentWeekStart) setCurrentWeekStart(ws);
  };
  const dismissFirstRun = () => {
    setShowFirstRun(false);
    setFirstRunDismissed(true);
    localStorage.setItem('planner_first_run_dismissed', '1');
  };

  const isCurrentWeek = currentWeekStart === weekStartOf(today);
  const weekDates = weekData ? Object.keys(weekData.days).sort() : [];
  const currentDayData = weekData?.days?.[currentDayStr] || null;
  const pct = progress ? Math.round((progress.covered / progress.total) * 100) : 0;

  return (
    <div className="planner-page">

      {/* Header */}
      <div className="planner-top">
        <div>
          <div className="planner-title">Planner</div>
          <div className="planner-week-label">Week of {formatWeekLabel(currentWeekStart)}</div>
        </div>
        <div className="planner-top-right">
          <div className="view-toggle">
            <button
              className={`view-btn${viewMode === 'week' ? ' view-btn-active' : ''}`}
              onClick={() => setViewMode('week')}
            >Week</button>
            <button
              className={`view-btn${viewMode === 'day' ? ' view-btn-active' : ''}`}
              onClick={() => setViewMode('day')}
            >Day</button>
          </div>
          <div className="week-nav">
            <button className="week-nav-btn" onClick={goToPrevWeek}>← Prev</button>
            {!isCurrentWeek && (
              <button className="week-nav-btn week-nav-btn-active" onClick={goToThisWeek}>This week</button>
            )}
            <button className="week-nav-btn" onClick={goToNextWeek}>Next →</button>
          </div>
        </div>
      </div>

      {/* First run / summary banners */}
      {showFirstRun && !firstRunDismissed && (
        <FirstRunBanner total={progress?.total || 497} onDismiss={dismissFirstRun} />
      )}
      {weekData?.is_current_week && showSummary && summary && (
        <WeeklySummaryCard summary={summary} onDismiss={() => setShowSummary(false)} />
      )}

      {/* Progress */}
      {progress && (
        <div className="planner-progress">
          <div className="planner-progress-text">{progress.covered} of {progress.total} topics covered ({pct}%)</div>
          <div className="planner-progress-bar"><div className="planner-progress-fill" style={{ width: `${pct}%` }} /></div>
        </div>
      )}

      {/* Feasibility / status banners */}
      {!loading && weekData?.feasibility?.status === 'tight' && (
        <div className="planner-feasibility-banner planner-feasibility-banner--tight">
          {weekData.feasibility.message}{' '}<a href="/settings" className="planner-feasibility-link">Adjust in Settings →</a>
        </div>
      )}
      {!loading && weekData?.feasibility?.status === 'unrealistic' && (
        <div className="planner-feasibility-banner planner-feasibility-banner--unrealistic">
          {weekData.feasibility.message}{' '}<a href="/settings" className="planner-feasibility-link">Adjust in Settings →</a>
        </div>
      )}
      {!loading && weekData?.is_revision_period && (
        <div className="planner-revision-period-bar">Revision period — focusing on your weakest areas</div>
      )}
      {!loading && weekData?.no_exam_date && (
        <div className="planner-no-exam-message">
          Set your exam date in Settings to generate your plan.{' '}
          <a href="/settings" className="planner-feasibility-link">Go to Settings →</a>
        </div>
      )}
      {!loading && weekData?.error_generating && !weekData?.no_exam_date && (
        <div className="planner-error">There was a problem generating your plan. Try regenerating in Settings.</div>
      )}

      {/* FLK filter */}
      {!loading && weekData && (
        <div className="planner-filters">
          {['all', 'FLK1', 'FLK2'].map(f => (
            <button
              key={f}
              className={flkFilter === f ? 'pill-filter-active' : 'pill-filter'}
              onClick={() => setFlkFilter(f)}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      )}

      {/* Detail toggle bar */}
      {!loading && weekData && (
        <div className="toggle-bar">
          <span className="toggle-label">Show:</span>
          {[
            { key: 'l2',        label: 'L2 group' },
            { key: 'depth',     label: 'Depth' },
            { key: 'questions', label: 'Questions' },
            { key: 'lastseen',  label: 'Last seen' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`toggle-chip${toggles[key] ? ' toggle-chip-on' : ''}`}
              onClick={() => flipToggle(key)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="planner-loading">Loading your plan</div>}
      {error && <div className="planner-error">{error}</div>}

      {/* Week view */}
      {!loading && weekData && viewMode === 'week' && (
        <>
          {weekData?.is_projected && (
            <div className="week-projected">Projected — will adjust as you add data</div>
          )}
          <div className="week-grid-wrapper">
            {weekScrollLeft && (
              <button className="week-scroll-btn week-scroll-btn--left" onClick={() => scrollWeekGrid(-1)}>‹</button>
            )}
            {weekScrollRight && (
              <button className="week-scroll-btn week-scroll-btn--right" onClick={() => scrollWeekGrid(1)}>›</button>
            )}
            <div className="week-grid" ref={weekGridRef} style={{ overflowX: 'auto' }}>
              {weekDates.map(dateStr => {
                const dayData = weekData.days[dateStr];
                if (!dayData) return null;
                return (
                  <DayColumn
                    key={dateStr}
                    dateStr={dateStr}
                    dayData={dayData}
                    allWeekDates={weekDates}
                    onComplete={handleComplete}
                    onMove={handleMove}
                    onAddEntry={handleAddEntry}
                    isToday={dateStr === today}
                    isPast={dateStr < today}
                    flkFilter={flkFilter}
                    toggles={toggles}
                    onSwitchToDayView={ds => { setCurrentDayStr(ds); setViewMode('day'); }}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Day view */}
      {!loading && weekData && viewMode === 'day' && (
        <DayViewPane
          dateStr={currentDayStr}
          dayData={currentDayData}
          allWeekDates={weekDates}
          onComplete={handleComplete}
          onMove={handleMove}
          onAddEntry={handleAddEntry}
          isToday={currentDayStr === today}
          isPast={currentDayStr < today}
          onPrev={() => navigateToDay(addDays(currentDayStr, -1))}
          onNext={() => navigateToDay(addDays(currentDayStr, 1))}
          daySort={daySort}
          setDaySort={setDaySort}
          hideCompleted={hideCompleted}
          setHideCompleted={setHideCompleted}
          flkFilter={flkFilter}
          toggles={toggles}
          onNavigateToTopic={onNavigateToTopic}
        />
      )}

      {!loading && !weekData && !error && (
        <div className="planner-empty">No plan data. Your schedule will appear here once generated.</div>
      )}
    </div>
  );
}
